"use client";

import React, { useEffect, useState } from "react";
import { Button } from "../ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "../ui/card";
import { Label } from "../ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../ui/select";
import { Input } from "../ui/input";
import { Database, Play, Loader2, ArrowLeft, Upload, Search, MessageSquare } from "lucide-react";
import { ReviewComment } from "../shared/ReviewComment";
import { 
  getProcedureFileUsageForWorkspace, 
  updateProcedureFileUsageForWorkspace,
  type FileUsageEntry 
} from "@/utils/fileUsageReference";
import { getFY_YEAR } from "@/utils/sharepoint-env";

interface IAIPETestingProps {
  onBack?: () => void;
}

interface ClientFile {
  name: string;
  reference: string;
}

interface ExcelColumn {
  name: string;
  value: string;
}

const FIELD_KEYS: Array<{ key: string; label: string }> = [
  { key: "asset_code", label: "Asset Code" },
  { key: "asset_category", label: "Asset Category" },
  { key: "asset_description", label: "Asset Description" },
  { key: "useful_life", label: "Useful Life" },
  { key: "original_cost", label: "Original Cost" },
  { key: "accumulated_depreciation", label: "Accumulated Depreciation" },
  { key: "net_book_value", label: "Net Book Value" },
  { key: "capitalization_date", label: "Capitalization Date" },
  { key: "scrap_value", label: "Scrap Value" },
  { key: "additions", label: "Additions" },
  { key: "deletions", label: "Deletions" },
  { key: "month_year", label: "Month/Year" },
];

export default function IAIPETesting({ onBack }: IAIPETestingProps) {
  const [excelColumns, setExcelColumns] = useState<ExcelColumn[]>([]);
  const [isLoadingExcelColumns, setIsLoadingExcelColumns] = useState(false);
  const [selectedFile, setSelectedFile] = useState<string>("");
  const [columnMappings, setColumnMappings] = useState<Record<string, string>>({});
  const [processingStatus, setProcessingStatus] = useState<"idle" | "running" | "completed" | "error">("idle");
  const [fileSearchStatus, setFileSearchStatus] = useState<"idle" | "searching" | "found" | "not_found">("idle");
  const [foundFiles, setFoundFiles] = useState<string[]>([]);
  const [referenceValue, setReferenceValue] = useState<string>("");
  const [uploadStatus, setUploadStatus] = useState<"idle" | "uploading" | "completed" | "error">("idle");
  const [reviewCommentQuestionId, setReviewCommentQuestionId] = useState<string | null>(null);
  const [monthYearSelection, setMonthYearSelection] = useState<string>("");
  const [fileUsageReference, setFileUsageReference] = useState<FileUsageEntry[]>([]);

  // Generate question ID for a section
  const getSectionQuestionId = (sectionName: string): string => {
    const normalizedSection = sectionName
      .replace(/[^a-zA-Z0-9\s]/g, "")
      .replace(/\s+/g, "_")
      .toUpperCase();
    return `IA_IPE_TESTING_${normalizedSection}`;
  };

  const loadFileUsageReference = async () => {
    try {
      const usage = await getProcedureFileUsageForWorkspace("Intangible Assets", "IPE Testing");
      setFileUsageReference(usage);
    } catch (error) {
      console.error("Error loading file usage reference:", error);
    }
  };

  // Auto-trigger search on mount
  useEffect(() => {
    searchForFiles();
    loadFileUsageReference();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const searchForFiles = async () => {
    setFileSearchStatus("searching");
    console.log(" Starting file search for Intangible Assets...");
    
    try {
      // Use the existing SharePoint API call but with the new section
      if (window.sharePointAPI?.loadComcFiles) {
        const result = await window.sharePointAPI.loadComcFiles("Execution_IT_IntAR");
        console.log(" SharePoint API result:", result);
        
        if (result.success && result.data?.files) {
          const files = result.data.files
            .map((f: any) => f.name)
            .filter((name: string) => name && name.trim().length > 0);
          
          if (files.length > 0) {
            console.log(" Files found:", files);
            setFoundFiles(files);
            setFileSearchStatus("found");
          } else {
            console.log(" No files found");
            setFileSearchStatus("not_found");
          }
        } else {
          console.log(" API call failed:", result.error);
          setFileSearchStatus("not_found");
        }
      } else {
        console.error("SharePoint API not available");
        setFileSearchStatus("not_found");
      }
    } catch (e) {
      console.error("Error searching for Intangible Assets files:", e);
      setFileSearchStatus("not_found");
    }
  };

  const uploadFile = async () => {
    setUploadStatus("uploading");
    try {
      const input = document.createElement('input');
      input.type = 'file';
      input.accept = '.xlsx,.xls,.csv';
      input.onchange = async (e) => {
        const file = (e.target as HTMLInputElement).files?.[0];
        if (file) {
          try {
            // Convert file to base64 as required by existing API
            const base64Content = await new Promise<string>((resolve, reject) => {
              const reader = new FileReader();
              reader.onload = () => {
                const result = reader.result as string;
                const base64 = result.split(',')[1];
                resolve(base64);
              };
              reader.onerror = () => reject(new Error('Failed to read file'));
              reader.readAsDataURL(file);
            });

            // Use the existing SharePoint API call with the new section
            if (window.sharePointAPI?.uploadFile) {
              const result = await window.sharePointAPI.uploadFile({
                fileContent: base64Content,
                fileName: file.name,
                referenceValue: referenceValue,
                folderName: "client",
                fyYear: getFY_YEAR(),
                sectionList: ["Execution_IT_FAR"]
              });

              if (result.success) {
                console.log(" File uploaded successfully:", file.name);
                setSelectedFile(file.name);
                setFoundFiles([file.name]);
                setFileSearchStatus("found");
                setUploadStatus("completed");
              } else {
                console.error(" Upload failed:", result.error);
                setUploadStatus("error");
              }
            } else {
              console.error("SharePoint API not available");
              setUploadStatus("error");
            }
          } catch (error) {
            console.error("Error uploading file:", error);
            setUploadStatus("error");
          }
        }
      };
      input.click();
    } catch (error) {
      console.error("Error in uploadFile:", error);
      setUploadStatus("error");
    }
  };

  const loadExcelColumns = async () => {
    if (!selectedFile) return;
    setIsLoadingExcelColumns(true);
    try {
      if (window.payroll?.loadExcelColumns) {
        const result = await window.payroll.loadExcelColumns(selectedFile);
        if (result.ok && result.columns) {
          const columns = result.columns.map((col: string) => ({ name: col, value: col }));
          setExcelColumns(columns);
          // Save file name to file usage reference
          const fileName = selectedFile.includes(' (') ? selectedFile.split(' (')[0] : selectedFile;
          await updateProcedureFileUsageForWorkspace("Intangible Assets", "IPE Testing", [{ 
            "Search for files": fileName 
          }]);
          await loadFileUsageReference();
        }
      } else {
        const testColumns = [
          "Asset Code", "Asset Category", "Asset Description", "Useful Life",
          "Original Cost", "Accumulated Depreciation", "Net Book Value", "Capitalization Date",
          "Scrap Value", "Additions", "Deletions", "Month/Year"
        ];
        const columns = testColumns.map((col: string) => ({ name: col, value: col }));
        setExcelColumns(columns);
      }
    } catch {
      const testColumns = [
        "Asset Code", "Asset Category", "Asset Description", "Useful Life",
        "Original Cost", "Accumulated Depreciation", "Net Book Value", "Capitalization Date",
        "Scrap Value", "Additions", "Deletions", "Month/Year"
      ];
      const columns = testColumns.map((col: string) => ({ name: col, value: col }));
      setExcelColumns(columns);
    } finally {
      setIsLoadingExcelColumns(false);
    }
  };

  const handleMappingChange = (fieldKey: string, columnName: string) => {
    setColumnMappings(prev => ({ ...prev, [fieldKey]: columnName }));
  };

  const isMonthYearMapped = monthYearSelection.length > 0;
  const allFieldsMapped = FIELD_KEYS.every(f => {
    if (f.key === "month_year") {
      return isMonthYearMapped;
    }
    return columnMappings[f.key] && columnMappings[f.key].length > 0;
  });

  const executeIPE = async () => {
    if (!selectedFile || !allFieldsMapped) return;
    
    // Save column mappings to file usage reference
    const entries: FileUsageEntry[] = [];
    const fileName = selectedFile.includes(' (') ? selectedFile.split(' (')[0] : selectedFile;
    entries.push({ "Search for files": fileName });
    const columnMapping: Record<string, any> = {};
    FIELD_KEYS.forEach(field => {
      if (field.key === "month_year") {
        columnMapping[field.label] = monthYearSelection === "Month" ? "Month" : "Year";
      } else if (columnMappings[field.key]) {
        columnMapping[field.label] = columnMappings[field.key];
      }
    });
    if (Object.keys(columnMapping).length > 0) {
      entries.push({ "Column Mapping": JSON.stringify(columnMapping) });
    }
    if (entries.length > 0) {
      await updateProcedureFileUsageForWorkspace("Intangible Assets", "IPE Testing", entries);
      await loadFileUsageReference();
    }
    
    setProcessingStatus("running");

    try {
      const customKeys = FIELD_KEYS.map(f => {
        if (f.key === "month_year") {
          // Convert Month/Year selection to boolean: Month = true, Year = false
          return monthYearSelection === "Month" ? true : false;
        }
        return columnMappings[f.key] || "";
      });
      const result = await window.payroll.run("execute_ia_ipe_testing", {
        inputFiles: [],
        options: {
          ipe_custom_keys: customKeys,
          reference_value: selectedFile || ""
        }
      });

      if (result.ok) {
        setProcessingStatus("completed");
      } else {
        setProcessingStatus("error");
      }
    } catch (e) {
      setProcessingStatus("error");
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-white">IPE Testing - Intangible Assets</h2>
          <p className="text-gray-400">Map FAR columns and update SharePoint column map</p>
        </div>
        {onBack && (
          <Button variant="outline" onClick={onBack}>
            <ArrowLeft className="h-4 w-4 mr-2" />
            Back
          </Button>
        )}
      </div>

      <Card className="border-white/10 bg-black/40">
        <CardHeader>
          <div className="flex items-center justify-between">
            <CardTitle className="flex items-center gap-3 text-white">
              <Search className="h-5 w-5 text-blue-500" />
              Step 1: Search for Existing Files
            </CardTitle>
            <Button
              type="button"
              variant="ghost"
              size="sm"
              onClick={() => setReviewCommentQuestionId(getSectionQuestionId("Step 1: Search for Existing Files"))}
              className="flex-shrink-0 p-1 h-7 w-7 text-yellow-400 hover:text-yellow-300 hover:bg-white/10"
              title="Review Comment"
            >
              <MessageSquare className="h-4 w-4" />
            </Button>
          </div>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex items-center gap-3">
            {fileSearchStatus === "found" && (
              <span className="text-green-400 font-bold"> FOUND!</span>
            )}
            {fileSearchStatus === "not_found" && (
              <span className="text-yellow-400">No files found</span>
            )}
          </div>

          {fileSearchStatus === "found" && (
            <div className="space-y-4">
              <div className="space-y-2">
                <Label className="text-white">Select File</Label>
                <Select value={selectedFile} onValueChange={setSelectedFile}>
                  <SelectTrigger>
                    <SelectValue placeholder="Select file" />
                  </SelectTrigger>
                  <SelectContent className="border-white/10 bg-black/90 text-white">
                    {foundFiles.map((f) => (
                      <SelectItem key={f} value={f}>
                        {f}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {fileUsageReference.find(e => e["Search for files"]) && (
                  <p className="text-xs text-white/60 mt-1">
                    Last used: {fileUsageReference.find(e => e["Search for files"])?.["Search for files"]}
                  </p>
                )}
              </div>
              {selectedFile && (
                <div className="flex items-center gap-3">
                  <Button onClick={loadExcelColumns} disabled={!selectedFile || isLoadingExcelColumns} className="bg-blue-600 hover:bg-blue-700 text-white">
                    {isLoadingExcelColumns ? (
                      <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Loading Columns...</>
                    ) : (
                      "Load Columns"
                    )}
                  </Button>
                  {excelColumns.length > 0 && (
                    <span className="text-green-400 text-sm"> {excelColumns.length} columns loaded</span>
                  )}
                </div>
              )}
            </div>
          )}

          {fileSearchStatus === "not_found" && (
            <div className="space-y-4 p-4 border border-yellow-500/20 bg-yellow-500/5 rounded-lg">
              <div className="text-yellow-400 font-medium">File not found. Please upload a file.</div>
              <div className="space-y-3">
                <div className="space-y-2">
                  <Label className="text-white">Reference Value (optional)</Label>
                  <Input
                    value={referenceValue}
                    onChange={(e) => setReferenceValue(e.target.value)}
                    placeholder="Enter reference value"
                    className="border-white/10 bg-black/40 text-white"
                  />
                </div>
                <Button onClick={uploadFile} disabled={uploadStatus === "uploading"} className="bg-green-600 hover:bg-green-700 text-white">
                  {uploadStatus === "uploading" ? (
                    <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Uploading...</>
                  ) : (
                    <><Upload className="h-4 w-4 mr-2" /> Upload File</>
                  )}
                </Button>
                {uploadStatus === "completed" && (
                  <span className="text-green-400">File uploaded successfully!</span>
                )}
                {uploadStatus === "error" && (
                  <span className="text-red-400">Upload failed</span>
                )}
              </div>
            </div>
          )}

        </CardContent>
      </Card>

      <Card className="border-white/10 bg-black/40">
        <CardHeader>
          <div className="flex items-center justify-between">
            <CardTitle className="text-white">Step 2: Map Columns</CardTitle>
            <Button
              type="button"
              variant="ghost"
              size="sm"
              onClick={() => setReviewCommentQuestionId(getSectionQuestionId("Step 2: Map Columns"))}
              className="flex-shrink-0 p-1 h-7 w-7 text-yellow-400 hover:text-yellow-300 hover:bg-white/10"
              title="Review Comment"
            >
              <MessageSquare className="h-4 w-4" />
            </Button>
          </div>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {FIELD_KEYS.map((field) => {
              // Special handling for month_year field
              if (field.key === "month_year") {
                return (
                  <div key={field.key} className="space-y-2">
                    <Label className="text-white">{field.label}</Label>
                    <Select
                      value={monthYearSelection}
                      onValueChange={setMonthYearSelection}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="Select Month or Year" />
                      </SelectTrigger>
                      <SelectContent className="border-white/10 bg-black/90 text-white">
                        <SelectItem value="Month">Month</SelectItem>
                        <SelectItem value="Year">Year</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                );
              }
              
              // Regular column mapping for other fields
              return (
                <div key={field.key} className="space-y-2">
                  <Label className="text-white">{field.label}</Label>
                  <Select
                    value={columnMappings[field.key] || ""}
                    onValueChange={(v) => handleMappingChange(field.key, v)}
                    disabled={excelColumns.length === 0}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder={excelColumns.length === 0 ? "Load columns first" : "Select column"} />
                    </SelectTrigger>
                    <SelectContent className="border-white/10 bg-black/90 text-white">
                      {excelColumns.map((col) => (
                        <SelectItem key={col.value} value={col.value}>{col.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  {fileUsageReference.find(e => e["Column Mapping"]) && (() => {
                    try {
                      const mapping = JSON.parse(fileUsageReference.find(e => e["Column Mapping"])?.["Column Mapping"] || "{}");
                      const lastUsed = mapping[field.label];
                      return lastUsed ? (
                        <p className="text-xs text-white/60 mt-1">
                          Last used: {lastUsed}
                        </p>
                      ) : null;
                    } catch {
                      return null;
                    }
                  })()}
                </div>
              );
            })}
          </div>
        </CardContent>
      </Card>

      <Card className="border-white/10 bg-black/40">
        <CardHeader>
          <div className="flex items-center justify-between">
            <CardTitle className="flex items-center gap-3 text-white">
              <Play className="h-5 w-5 text-green-500" />
              Execute IPE Mapping
            </CardTitle>
            <Button
              type="button"
              variant="ghost"
              size="sm"
              onClick={() => setReviewCommentQuestionId(getSectionQuestionId("Execute IPE Mapping"))}
              className="flex-shrink-0 p-1 h-7 w-7 text-yellow-400 hover:text-yellow-300 hover:bg-white/10"
              title="Review Comment"
            >
              <MessageSquare className="h-4 w-4" />
            </Button>
          </div>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex items-center gap-3">
            <Button onClick={executeIPE} disabled={!selectedFile || !allFieldsMapped || processingStatus === "running"} className="bg-green-600 hover:bg-green-700 text-white">
              {processingStatus === "running" ? (
                <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Processing...</>
              ) : (
                "Run IPE Testing"
              )}
            </Button>
            {processingStatus === "completed" && (
              <span className="text-green-400">Successful</span>
            )}
            {processingStatus === "error" && (
              <span className="text-red-400">Failed</span>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Review Comment Modal */}
      {reviewCommentQuestionId && (
        <ReviewComment
          questionId={reviewCommentQuestionId}
          questionText={(() => {
            // Map questionId back to section name for display
            const sectionMap: Record<string, string> = {
              [getSectionQuestionId("Step 1: Search for Existing Files")]: "Step 1: Search for Existing Files",
              [getSectionQuestionId("Step 2: Map Columns")]: "Step 2: Map Columns",
              [getSectionQuestionId("Execute IPE Mapping")]: "Execute IPE Mapping",
            };
            return sectionMap[reviewCommentQuestionId] || reviewCommentQuestionId;
          })()}
          isOpen={!!reviewCommentQuestionId}
          onClose={() => setReviewCommentQuestionId(null)}
        />
      )}
    </div>
  );
}



